import { RUN_ERROR_LABELS } from "../constants/pageAgentLabels";

/** What went wrong with a run, and whether it means the agent is switched off here. */
export interface RunFailure {
  message: string;
  /** True for a 503: the "not configured here" state, not an error. */
  unavailable: boolean;
}

const UNAUTHORIZED_STATUS = 401;
const UNAVAILABLE_STATUS = 503;

/**
 * Reads a failed run's error.
 *
 * On a non-2xx `HttpAgent` parses the body itself and hangs `status` and `payload` on the `Error`;
 * `payload` is the module's `{ status, code, message }` body — present only because `aguiClient`
 * asks for `application/json` beside the event stream.
 */
export function describeRunFailure(err: unknown): RunFailure {
  const status = numberField(err, "status");
  const message = stringField(field(err, "payload"), "message");
  if (status === UNAUTHORIZED_STATUS) return { message: RUN_ERROR_LABELS.expiredSession, unavailable: false };
  if (status === UNAVAILABLE_STATUS) return { message: RUN_ERROR_LABELS.unavailable, unavailable: true };
  if (message === undefined || message.trim() === "") {
    console.warn("AG-UI run failed without a reader-facing message", { status, err });
    return { message: RUN_ERROR_LABELS.unreachable, unavailable: false };
  }
  return { message, unavailable: false };
}

function field(value: unknown, name: string): unknown {
  return value !== null && typeof value === "object" && name in value
    ? (value as Record<string, unknown>)[name] : undefined;
}

function stringField(value: unknown, name: string): string | undefined {
  const found = field(value, name);
  return typeof found === "string" ? found : undefined;
}

function numberField(value: unknown, name: string): number | undefined {
  const found = field(value, name);
  return typeof found === "number" ? found : undefined;
}
